// `TurnDelta` from two consecutive store snapshots (PLAN-CLIENT-SPLIT §2.2).
//
// **The store has no event payloads — it says "something changed" and hands
// out the whole snapshot — so what happened is a diff of the two.** The tests
// are the renderer's own played/ticked/free inference, `castleRookMove`, the
// bash and drag legs and the carry reports, moved here verbatim so
// there is one reading of "what just happened" and the client is handed it.
//
// The order of the questions is load-bearing:
//
//  1. **The view moved** — a history step, or the store snapping the viewer
//     back to live. A snap, tinted with what the store measured.
//  2. **One command landed** — the log grew by one on the same prefix. A new
//     game is a load; otherwise a move, a tick or a free change, told apart
//     exactly as the renderer told them: `history` grew by one is a move,
//     `lastTick`'s identity changed is a tick, neither is free.
//  3. **Anything else** is a cut or a replacement: an undo or a rewind carries
//     the store's diff and is a snap; a load, a restore and everything the
//     store cleared the tint for is a load.

import type { Leg, Side, Square, TickFacts, TurnDelta } from "src/api";
import type { GameSnapshot } from "src/engine/game/GameStore";
import { fileOf, pieceType, type PieceType, rankOf, square } from "src/engine/rules/board";
import { blowStep } from "src/engine/rules/chess/blow";
import { dragSquare } from "src/engine/rules/chess/clasp";
import type { GameState } from "src/engine/rules/commands";
import {
  isBash,
  isCapture,
  isCastle,
  isDrag,
  isStructureCapture,
  type Move,
  moveFrom,
  moveTo,
  victimOf
} from "src/engine/rules/move";
import { pieceIdAt } from "src/engine/rules/position";
import type { TickReport } from "src/engine/rules/tick";
import { asSide, asSquare, PIECE_NAME, STRUCTURE_NAME } from "./names";

/** Rook from/to for a castle, so the rook animates with the king. */
function castleRookMove(kingTo: number): { from: number; to: number } {
  const rank = rankOf(kingTo);
  const kingSide = fileOf(kingTo) === 6;
  return kingSide
    ? { from: square(7, rank), to: square(5, rank) }
    : { from: square(0, rank), to: square(3, rank) };
}

/** The rank standing on `sq` *after* the change — the piece that arrived there. */
const rankAt = (state: GameState, sq: number): PieceType =>
  (pieceType(state.position.board.get(sq) ?? 0) || 1) as PieceType;

const leg = (state: GameState, from: number, to: number, role: Leg["role"]): Leg => {
  const id = pieceIdAt(state.position, to);
  return {
    ...(id ? { id } : {}),
    piece: PIECE_NAME[rankAt(state, to)],
    from: asSquare(from),
    to: asSquare(to),
    role
  };
};

/** The legs one move makes: the mover, and whatever it took with it. */
function moveLegs(next: GameState, move: Move): Leg[] {
  const from = moveFrom(move);
  const to = moveTo(move);
  const legs: Leg[] = [leg(next, from, to, "mover")];
  if (isCastle(move)) {
    const rook = castleRookMove(to);
    legs.push(leg(next, rook.from, rook.to, "castle-rook"));
  }
  // A shove that landed: the attacker took the target's square and the target
  // went one further. A refused shove moves nobody but the attacker, and then
  // `shoved` is not where the attacker ended up.
  if (isBash(move) && to === move.shoved) {
    legs.push(leg(next, move.shoved, move.shoved + blowStep(from, move.shoved), "shoved"));
  }
  if (isDrag(move)) legs.push(leg(next, move.dragged, dragSquare(from, to, move.dragged), "dragged"));
  return legs;
}

export function tickFacts(next: GameState, report: TickReport): TickFacts {
  const cooking = [...next.industry.cooking.keys()].filter((sq) => (next.industry.cooking.get(sq)?.length ?? 0) > 0);
  return {
    cargo: (report.cargo ?? []).map((m) => ({
      from: asSquare(m.from),
      to: asSquare(m.to),
      fromSlot: m.fromIndex,
      toSlot: m.toIndex,
      phase: m.phase
    })),
    shots: (report.shots ?? []).map((s) => ({ from: asSquare(s.from), to: asSquare(s.to), killed: s.killed })),
    unmasked: (report.unmasked ?? []).map((u) => asSquare(u.square)),
    minted: report.minted.map((m) => ({ square: asSquare(m.square), type: PIECE_NAME[m.type], by: asSquare(m.at) })),
    placed: (report.placed ?? []).map((p) => ({ square: asSquare(p.square), kind: STRUCTURE_NAME[p.kind] })),
    ...(report.collected ? { collected: { at: asSquare(report.collected.at), amount: report.collected.amount } } : {}),
    mined: report.mined,
    cooking: cooking.sort((a, b) => a - b).map(asSquare)
  };
}

/** What a move took off the board: a piece, or a building captured like one. */
function captures(move: Move, side: number): Square[] {
  if (isCapture(move)) return [asSquare(victimOf(move, side))];
  if (isStructureCapture(move)) return [asSquare(moveTo(move))];
  return [];
}

/** The log grew by exactly one command on the same prefix. */
function appended(prev: GameSnapshot, next: GameSnapshot): boolean {
  if (next.log.length !== prev.log.length + 1) return false;
  return prev.log.length === 0 || next.log[0] === prev.log[0];
}

const squares = (list: readonly number[] | null | undefined): Square[] => (list ?? []).map(asSquare);

export function turnDelta(prev: GameSnapshot, next: GameSnapshot): TurnDelta {
  const viewMoved = next.history.view !== prev.history.view || next.history.viewed !== prev.history.viewed;
  const before = prev.state;
  const after = next.state;

  if (!appended(prev, next)) {
    if (viewMoved) return { kind: "snap", changed: squares(next.history.changed) };
    // A cut carries the store's diff; a replacement cleared it.
    const changed = next.history.changed;
    if (changed && changed.length > 0) return { kind: "snap", changed: squares(changed) };
    return { kind: "load" };
  }

  const command = next.log[next.log.length - 1];
  if (command.type === "new-game") return { kind: "load" };
  // A step while a command lands is the store snapping the viewer back to
  // live, and the mover's animation is still owed — fall through with the
  // store's tint, which is what a snap back would have shown.

  const played = after.history.length === before.history.length + 1;
  const ticked =
    after.lastTick !== undefined &&
    after.lastTick !== before.lastTick &&
    after.history.length >= before.history.length;
  const side: Side = asSide(before.position.turn);
  const tint = next.history.changed;

  if (played) {
    const move = after.history[after.history.length - 1];
    const legs = moveLegs(after, move);
    return {
      kind: "move",
      side,
      legs,
      captures: captures(move, before.position.turn),
      ...(ticked && after.lastTick ? { tick: tickFacts(after, after.lastTick) } : {}),
      changed: tint ? squares(tint) : [asSquare(moveFrom(move)), asSquare(moveTo(move))]
    };
  }
  if (ticked && after.lastTick) {
    return {
      kind: "tick",
      side,
      legs: [],
      tick: tickFacts(after, after.lastTick),
      ...(tint ? { changed: squares(tint) } : {})
    };
  }
  if (viewMoved) return { kind: "snap", changed: squares(tint) };
  return { kind: "free" };
}
